import React, { FC } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Button,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { Add, DeleteOutline, ExpandMore } from '@mui/icons-material';
import { Control, Controller, FieldValues, useFieldArray, useWatch } from 'react-hook-form';
import { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { ColoredThreshold, MutateWidgetPayload, WidgetSettingsType } from '@thingslog/repositories';
import { WidgetFieldPath } from './query-settings/models';

interface Props {
  control: Control<MutateWidgetPayload>;
  disabled?: boolean;
  widgetType: WidgetSettingsType;
}

interface SelectOption {
  label: string;
  value: string;
}

const lineThicknessOptions: SelectOption[] = [
  { value: 'fine', label: 'Fine' },
  { value: 'standard', label: 'Standard' },
  { value: 'bold', label: 'Bold' },
];

const lineInterpolationOptions: SelectOption[] = [
  { value: 'straight', label: 'Straight' },
  { value: 'smooth', label: 'Smooth' },
  { value: 'stepped', label: 'Stepped' },
];

const areaFillOptions: SelectOption[] = [
  { value: 'subtle', label: 'Subtle' },
  { value: 'balanced', label: 'Balanced' },
  { value: 'solid', label: 'Solid' },
];

const barOrientationOptions: SelectOption[] = [
  { value: 'vertical', label: 'Vertical' },
  { value: 'horizontal', label: 'Horizontal' },
];

const barGroupingOptions: SelectOption[] = [
  { value: 'grouped', label: 'Grouped' },
  { value: 'stacked', label: 'Stacked' },
];

const barThicknessOptions: SelectOption[] = [
  { value: 'slim', label: 'Slim' },
  { value: 'regular', label: 'Regular' },
  { value: 'wide', label: 'Wide' },
];

const pieShapeOptions: SelectOption[] = [
  { value: 'pie', label: 'Pie' },
  { value: 'donut', label: 'Donut' },
];

const gaugeTypeOptions: SelectOption[] = [
  { value: 'dial', label: 'Dial' },
  { value: 'bar', label: 'Bar' },
];

const HEX_COLOR_PATTERN = /^#[0-9A-Fa-f]{6}$/;
const MAX_LINE_CHART_POINT_DOTS = 100;

const normalizeColor = (value: unknown, fallback: string): string => {
  return typeof value === 'string' && HEX_COLOR_PATTERN.test(value) ? value : fallback;
};

const createDefaultThreshold = (index: number, t: TFunction): ColoredThreshold => ({
  value: 0,
  color: index === 1 ? '#EF4444' : '#F59E0B',
  label:
    index === 1
      ? t('dashboard_visualization_threshold_critical', 'Critical')
      : t('dashboard_visualization_threshold_warning', 'Warning'),
});

const SwitchField = ({
  control,
  disabled,
  label,
  name,
}: {
  control: Control<MutateWidgetPayload>;
  disabled?: boolean;
  label: string;
  name: WidgetFieldPath;
}): JSX.Element => (
  <Controller
    name={name}
    control={control}
    render={({ field }: FieldValues): JSX.Element => (
      <FormControlLabel
        control={
          <Switch
            checked={Boolean(field.value)}
            disabled={disabled}
            size="small"
            onChange={(_event: React.ChangeEvent<HTMLInputElement>, checked: boolean): void =>
              field.onChange(checked)
            }
          />
        }
        label={label}
      />
    )}
  />
);

const SelectField = ({
  control,
  disabled,
  label,
  name,
  options,
}: {
  control: Control<MutateWidgetPayload>;
  disabled?: boolean;
  label: string;
  name: WidgetFieldPath;
  options: SelectOption[];
}): JSX.Element => (
  <SelectFieldInner
    control={control}
    disabled={disabled}
    label={label}
    name={name}
    options={options}
  />
);

const SelectFieldInner = ({
  control,
  disabled,
  label,
  name,
  options,
}: {
  control: Control<MutateWidgetPayload>;
  disabled?: boolean;
  label: string;
  name: WidgetFieldPath;
  options: SelectOption[];
}): JSX.Element => {
  const { t } = useTranslation();

  return (
    <Controller
      name={name}
      control={control}
      render={({ field }: FieldValues): JSX.Element => (
        <FormControl fullWidth size="small">
          <InputLabel>{label}</InputLabel>
          <Select {...field} label={label} disabled={disabled}>
            {options.map((option: SelectOption) => (
              <MenuItem key={option.value} value={option.value}>
                {t(`dashboard_visualization_option_${option.value}`, option.label)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}
    />
  );
};

const NumberField = ({
  control,
  disabled,
  label,
  name,
}: {
  control: Control<MutateWidgetPayload>;
  disabled?: boolean;
  label: string;
  name: WidgetFieldPath;
}): JSX.Element => (
  <Controller
    name={name}
    control={control}
    render={({ field, fieldState }: FieldValues): JSX.Element => (
      <TextField
        {...field}
        type="number"
        label={label}
        size="small"
        fullWidth
        disabled={disabled}
        error={!!fieldState.error}
        helperText={fieldState.error?.message}
        onChange={(event: React.ChangeEvent<HTMLInputElement>): void =>
          field.onChange(Number(event.target.value))
        }
      />
    )}
  />
);

const TextInputField = ({
  control,
  disabled,
  label,
  name,
}: {
  control: Control<MutateWidgetPayload>;
  disabled?: boolean;
  label: string;
  name: WidgetFieldPath;
}): JSX.Element => (
  <Controller
    name={name}
    control={control}
    render={({ field }: FieldValues): JSX.Element => (
      <TextField {...field} label={label} size="small" fullWidth disabled={disabled} />
    )}
  />
);

const ColorField = ({
  control,
  disabled,
  label,
  name,
  fallback = '#3B82F6',
}: {
  control: Control<MutateWidgetPayload>;
  disabled?: boolean;
  label: string;
  name: WidgetFieldPath;
  fallback?: string;
}): JSX.Element => (
  <Controller
    name={name}
    control={control}
    render={({ field }: FieldValues): JSX.Element => (
      <TextField
        {...field}
        type="color"
        label={label}
        size="small"
        fullWidth
        disabled={disabled}
        value={normalizeColor(field.value, fallback)}
        InputLabelProps={{ shrink: true }}
        onChange={(event: React.ChangeEvent<HTMLInputElement>): void =>
          field.onChange(event.target.value)
        }
      />
    )}
  />
);

const OptionalNumberField = ({
  control,
  disabled,
  label,
  name,
}: {
  control: Control<MutateWidgetPayload>;
  disabled?: boolean;
  label: string;
  name: WidgetFieldPath;
}): JSX.Element => (
  <Controller
    name={name}
    control={control}
    render={({ field, fieldState }: FieldValues): JSX.Element => (
      <TextField
        {...field}
        type="number"
        label={label}
        size="small"
        fullWidth
        disabled={disabled}
        value={field.value ?? ''}
        error={!!fieldState.error}
        helperText={fieldState.error?.message}
        onChange={(event: React.ChangeEvent<HTMLInputElement>): void =>
          field.onChange(event.target.value === '' ? null : Number(event.target.value))
        }
      />
    )}
  />
);

const DEFAULT_VALUE_TEXT_COLOR = '#0F172A';

const ValueColorFields = ({
  control,
  disabled,
}: {
  control: Control<MutateWidgetPayload>;
  disabled?: boolean;
}): JSX.Element => {
  const { t } = useTranslation();

  return (
    <>
      <div className="grid min-w-0 grid-cols-[1fr_auto] gap-2">
        <div className="grid min-w-0 grid-cols-1 items-center gap-3 md:grid-cols-3">
          <Typography className="text-left" variant="body2">
            {t('dashboard_visualization_value_color', 'Base color')}
          </Typography>
          <div className="md:col-span-2">
            <ColorField
              control={control}
              disabled={disabled}
              label={t('dashboard_visualization_color', 'Color')}
              name={'settings.visualization.color' as WidgetFieldPath}
            />
          </div>
        </div>
        <IconButton className="invisible" disabled>
          <DeleteOutline fontSize="small" />
        </IconButton>
      </div>
      <div className="grid min-w-0 grid-cols-[1fr_auto] gap-2">
        <div className="grid min-w-0 grid-cols-1 items-center gap-3 md:grid-cols-3">
          <div className="flex items-center gap-2">
            <Typography className="text-left" variant="body2">
              {t('dashboard_visualization_value_text_color', 'Custom text color')}
            </Typography>
            <Controller
              name={'settings.visualization.textColor' as WidgetFieldPath}
              control={control}
              render={({ field }: FieldValues): JSX.Element => (
                <Switch
                  checked={field.value != null}
                  disabled={disabled}
                  size="small"
                  onChange={(_event: React.ChangeEvent<HTMLInputElement>, checked: boolean): void =>
                    field.onChange(checked ? DEFAULT_VALUE_TEXT_COLOR : null)
                  }
                />
              )}
            />
          </div>
          <div className="md:col-span-2">
            <Controller
              name={'settings.visualization.textColor' as WidgetFieldPath}
              control={control}
              render={({ field }: FieldValues): JSX.Element =>
                field.value != null ? (
                  <TextField
                    type="color"
                    label={t('dashboard_visualization_color', 'Color')}
                    size="small"
                    fullWidth
                    disabled={disabled}
                    value={normalizeColor(field.value, DEFAULT_VALUE_TEXT_COLOR)}
                    InputLabelProps={{ shrink: true }}
                    onChange={(event: React.ChangeEvent<HTMLInputElement>): void =>
                      field.onChange(event.target.value)
                    }
                  />
                ) : (
                  <div />
                )
              }
            />
          </div>
        </div>
        <IconButton className="invisible" disabled>
          <DeleteOutline fontSize="small" />
        </IconButton>
      </div>
    </>
  );
};

const ThresholdFields = ({
  control,
  disabled,
  headerLabel,
  children,
}: {
  control: Control<MutateWidgetPayload>;
  disabled?: boolean;
  headerLabel?: string;
  children?: React.ReactNode;
}): JSX.Element => {
  const { t } = useTranslation();
  const { fields, append, remove } = useFieldArray({
    control,
    name: 'settings.visualization.thresholds' as never,
  });

  const handleAddThreshold = (): void => {
    append(
      {
        ...createDefaultThreshold(fields.length, t),
        label: t('dashboard_visualization_threshold_named', 'Threshold {{index}}', {
          index: fields.length + 1,
        }),
      } as never,
      { shouldFocus: false }
    );
  };

  return (
    <div className="col-span-1 flex min-w-0 flex-col gap-3 md:col-span-2">
      <div className="flex items-center justify-between gap-3">
        <Typography variant="subtitle2">
          {headerLabel ?? t('dashboard_visualization_thresholds', 'Thresholds')}
        </Typography>
        <Button
          startIcon={<Add />}
          variant="outlined"
          size="small"
          disabled={disabled}
          onClick={handleAddThreshold}
        >
          {t('dashboard_visualization_add_threshold', 'Add threshold')}
        </Button>
      </div>

      {children}

      {fields.map((field: { id: string }, index: number) => {
        const defaultThreshold = createDefaultThreshold(index, t);

        return (
          <div
            key={field.id}
            className="grid min-w-0 grid-cols-[1fr_auto] gap-2 border-0 border-t border-solid border-gray-100 pt-3"
          >
            <div className="grid min-w-0 grid-cols-1 gap-3 md:grid-cols-3">
              <NumberField
                control={control}
                disabled={disabled}
                label={t('dashboard_visualization_level', 'Level')}
                name={`settings.visualization.thresholds.${index}.value` as WidgetFieldPath}
              />
              <TextInputField
                control={control}
                disabled={disabled}
                label={t('dashboard_visualization_label', 'Label')}
                name={`settings.visualization.thresholds.${index}.label` as WidgetFieldPath}
              />
              <ColorField
                control={control}
                disabled={disabled}
                label={t('dashboard_visualization_color', 'Color')}
                name={`settings.visualization.thresholds.${index}.color` as WidgetFieldPath}
                fallback={defaultThreshold.color}
              />
            </div>
            <IconButton
              aria-label={t('dashboard_visualization_remove_threshold', 'Remove threshold')}
              color="error"
              disabled={disabled}
              onClick={(): void => remove(index)}
            >
              <DeleteOutline fontSize="small" />
            </IconButton>
          </div>
        );
      })}
    </div>
  );
};

export const WidgetVisualizationForm: FC<Props> = ({
  control,
  disabled = false,
  widgetType,
}: Props): JSX.Element | null => {
  const { t } = useTranslation();
  const gaugeVisualizationType = useWatch({
    control,
    name: 'settings.visualization.visualizationType' as WidgetFieldPath,
  });
  const showLineChartPoints = useWatch({
    control,
    name: 'settings.visualization.showPoints' as WidgetFieldPath,
  });

  const showCartesianFields =
    widgetType === 'line-chart' || widgetType === 'area-chart' || widgetType === 'bar-chart';
  const showLineFields = widgetType === 'line-chart' || widgetType === 'area-chart';

  return (
    <Accordion
      elevation={0}
      disableGutters
      className="border-0 border-t border-solid border-gray-200"
      sx={{ '&:before': { display: 'none' } }}
    >
      <AccordionSummary
        expandIcon={<ExpandMore />}
        className="px-0"
        sx={{
          minHeight: 44,
          '& .MuiAccordionSummary-content': { justifyContent: 'center', my: 1.5 },
        }}
      >
        <div className="flex min-w-0 flex-col items-center text-center">
          <Typography variant="subtitle2">
            {t('dashboard_visualization', 'Visualization')}
          </Typography>
          <Typography variant="caption" className="text-gray-600">
            {t('dashboard_visualization_description', 'Display options and chart styling')}
          </Typography>
        </div>
      </AccordionSummary>
      <AccordionDetails className="grid min-w-0 grid-cols-1 gap-3 px-0 pb-0 pt-1 md:grid-cols-2">
        {showCartesianFields && (
          <>
            <SwitchField
              control={control}
              disabled={disabled}
              label={t('dashboard_visualization_show_grid', 'Show grid')}
              name={'settings.visualization.showGrid' as WidgetFieldPath}
            />
            <SwitchField
              control={control}
              disabled={disabled}
              label={t('dashboard_visualization_show_legend', 'Show legend')}
              name={'settings.visualization.showLegend' as WidgetFieldPath}
            />
            <SwitchField
              control={control}
              disabled={disabled}
              label={t('dashboard_visualization_render_legend_aside', 'Legend on the side')}
              name={'settings.visualization.renderLegendAside' as WidgetFieldPath}
            />
            <SwitchField
              control={control}
              disabled={disabled}
              label={t('dashboard_visualization_show_x_axis_labels', 'Show X axis labels')}
              name={'settings.visualization.showXAxisLabels' as WidgetFieldPath}
            />
            <SwitchField
              control={control}
              disabled={disabled}
              label={t('dashboard_visualization_show_y_axis_labels', 'Show Y axis labels')}
              name={'settings.visualization.showYAxisLabels' as WidgetFieldPath}
            />
          </>
        )}

        {showLineFields && (
          <>
            <SelectField
              control={control}
              disabled={disabled}
              label={t('dashboard_visualization_line_thickness', 'Line thickness')}
              name={'settings.visualization.lineThickness' as WidgetFieldPath}
              options={lineThicknessOptions}
            />
            <SelectField
              control={control}
              disabled={disabled}
              label={t('dashboard_visualization_interpolation', 'Interpolation')}
              name={'settings.visualization.interpolation' as WidgetFieldPath}
              options={lineInterpolationOptions}
            />
          </>
        )}

        {widgetType === 'line-chart' && (
          <>
            <SwitchField
              control={control}
              disabled={disabled}
              label={t('dashboard_visualization_show_points', 'Show points')}
              name={'settings.visualization.showPoints' as WidgetFieldPath}
            />
            {showLineChartPoints === true && (
              <Typography variant="caption" className="text-gray-600 md:col-span-2">
                {t(
                  'dashboard_visualization_points_hidden_helper',
                  'Points are automatically hidden when the time series has more than {{count}} data points for better visual clarity.',
                  { count: MAX_LINE_CHART_POINT_DOTS }
                )}
              </Typography>
            )}
          </>
        )}

        {widgetType === 'area-chart' && (
          <>
            <SwitchField
              control={control}
              disabled={disabled}
              label={t('dashboard_visualization_stack_series', 'Stack series')}
              name={'settings.visualization.stacked' as WidgetFieldPath}
            />
            <SelectField
              control={control}
              disabled={disabled}
              label={t('dashboard_visualization_fill_opacity', 'Fill opacity')}
              name={'settings.visualization.fillOpacity' as WidgetFieldPath}
              options={areaFillOptions}
            />
          </>
        )}

        {widgetType === 'bar-chart' && (
          <>
            <SelectField
              control={control}
              disabled={disabled}
              label={t('dashboard_visualization_orientation', 'Orientation')}
              name={'settings.visualization.orientation' as WidgetFieldPath}
              options={barOrientationOptions}
            />
            <SelectField
              control={control}
              disabled={disabled}
              label={t('dashboard_visualization_grouping', 'Grouping')}
              name={'settings.visualization.grouping' as WidgetFieldPath}
              options={barGroupingOptions}
            />
            <SelectField
              control={control}
              disabled={disabled}
              label={t('dashboard_visualization_bar_thickness', 'Bar thickness')}
              name={'settings.visualization.barThickness' as WidgetFieldPath}
              options={barThicknessOptions}
            />
          </>
        )}

        {widgetType === 'pie-chart' && (
          <>
            <SwitchField
              control={control}
              disabled={disabled}
              label={t('dashboard_visualization_show_legend', 'Show legend')}
              name={'settings.visualization.showLegend' as WidgetFieldPath}
            />
            <SwitchField
              control={control}
              disabled={disabled}
              label={t('dashboard_visualization_render_legend_aside', 'Legend on the side')}
              name={'settings.visualization.renderLegendAside' as WidgetFieldPath}
            />
            <SelectField
              control={control}
              disabled={disabled}
              label={t('dashboard_visualization_shape', 'Shape')}
              name={'settings.visualization.shape' as WidgetFieldPath}
              options={pieShapeOptions}
            />
          </>
        )}

        {widgetType === 'gauge' && (
          <>
            <SelectField
              control={control}
              disabled={disabled}
              label={t('dashboard_visualization_gauge_type', 'Gauge type')}
              name={'settings.visualization.visualizationType' as WidgetFieldPath}
              options={gaugeTypeOptions}
            />
            {gaugeVisualizationType === 'bar' && (
              <>
                <SelectField
                  control={control}
                  disabled={disabled}
                  label={t('dashboard_visualization_bar_orientation', 'Bar orientation')}
                  name={'settings.visualization.barOrientation' as WidgetFieldPath}
                  options={barOrientationOptions}
                />
                <SelectField
                  control={control}
                  disabled={disabled}
                  label={t('dashboard_visualization_bar_thickness', 'Bar thickness')}
                  name={'settings.visualization.barThickness' as WidgetFieldPath}
                  options={barThicknessOptions}
                />
              </>
            )}
            <NumberField
              control={control}
              disabled={disabled}
              label={t('dashboard_visualization_minimum', 'Minimum')}
              name={'settings.visualization.minValue' as WidgetFieldPath}
            />
            <NumberField
              control={control}
              disabled={disabled}
              label={t('dashboard_visualization_maximum', 'Maximum')}
              name={'settings.visualization.maxValue' as WidgetFieldPath}
            />
            <ColorField
              control={control}
              disabled={disabled}
              label={t('dashboard_visualization_gauge_color', 'Gauge color')}
              name={'settings.visualization.color' as WidgetFieldPath}
            />
            <SwitchField
              control={control}
              disabled={disabled}
              label={t('dashboard_visualization_show_value', 'Show value')}
              name={'settings.visualization.showValue' as WidgetFieldPath}
            />
            <SwitchField
              control={control}
              disabled={disabled}
              label={t('dashboard_visualization_show_min_max', 'Show min/max')}
              name={'settings.visualization.showMinMaxLabels' as WidgetFieldPath}
            />
            <SwitchField
              control={control}
              disabled={disabled}
              label={t('dashboard_visualization_show_legend', 'Show legend')}
              name={'settings.visualization.showLegend' as WidgetFieldPath}
            />
            <SwitchField
              control={control}
              disabled={disabled}
              label={t('dashboard_visualization_show_legend_aside', 'Legend on the side')}
              name={'settings.visualization.showLegendAside' as WidgetFieldPath}
            />
            <ThresholdFields control={control} disabled={disabled} />
          </>
        )}

        {widgetType === 'value' && (
          <>
            <TextInputField
              control={control}
              disabled={disabled}
              label={t('dashboard_visualization_value_prefix', 'Prefix')}
              name={'settings.visualization.prefix' as WidgetFieldPath}
            />
            <TextInputField
              control={control}
              disabled={disabled}
              label={t('dashboard_visualization_value_suffix', 'Suffix')}
              name={'settings.visualization.suffix' as WidgetFieldPath}
            />
            <OptionalNumberField
              control={control}
              disabled={disabled}
              label={t('dashboard_visualization_value_target', 'Target')}
              name={'settings.visualization.target' as WidgetFieldPath}
            />
            <SwitchField
              control={control}
              disabled={disabled}
              label={t('dashboard_visualization_value_show_target_percentage', 'Show % of target')}
              name={'settings.visualization.showTargetPercentage' as WidgetFieldPath}
            />
            <SwitchField
              control={control}
              disabled={disabled}
              label={t('dashboard_visualization_value_stacked', 'Prefix above, suffix below')}
              name={'settings.visualization.stacked' as WidgetFieldPath}
            />
            <SwitchField
              control={control}
              disabled={disabled}
              label={t('dashboard_visualization_value_compact', 'Compact numbers')}
              name={'settings.visualization.compact' as WidgetFieldPath}
            />
            <ThresholdFields
              control={control}
              disabled={disabled}
              headerLabel={t('dashboard_visualization_colors', 'Colors')}
            >
              <ValueColorFields control={control} disabled={disabled} />
            </ThresholdFields>
          </>
        )}

        {widgetType === 'table' && (
          <SwitchField
            control={control}
            disabled={disabled}
            label={t('dashboard_visualization_paginate_rows', 'Paginate rows')}
            name={'settings.visualization.paginated' as WidgetFieldPath}
          />
        )}
      </AccordionDetails>
    </Accordion>
  );
};
